import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SendIcon from '@mui/icons-material/Send';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { patientDetailsApi } from '../../services/api';
import { formatDateTime } from '../../utils/dateTime';
import { extractScheduling, extractDrafts, SchedulingSuggestion } from './scheduling';

export type { SchedulingSuggestion } from './scheduling';

interface ConsultationThread {
  id: string;
  patientId?: string;
  title: string;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
  lastMessageAt?: string;
}

interface ConsultationMessage {
  id: string;
  threadId: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
}

interface AIConsultationPanelProps {
  spruceEntityId?: string;
  patientName?: string;
  // When provided, a "Create appointment" action is shown next to any AI
  // scheduling suggestion; clicking it asks the host to open the scheduler.
  onCreateAppointment?: (suggestion: SchedulingSuggestion) => void;
  // When the host (e.g. the History timeline "Open" action) wants to jump to a
  // specific thread, it sets openThreadId. openThreadNonce changes on every
  // request so re-opening the same thread still triggers selection.
  openThreadId?: string;
  openThreadNonce?: number;
}

const AIConsultationPanel: React.FC<AIConsultationPanelProps> = ({ spruceEntityId, patientName, onCreateAppointment, openThreadId, openThreadNonce }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [threads, setThreads] = useState<ConsultationThread[]>([]);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ConsultationMessage[]>([]);
  const [input, setInput] = useState('');
  const [loadingThreads, setLoadingThreads] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameText, setRenameText] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const loadThreads = useCallback(
    async (autoSelect = true) => {
      if (!spruceEntityId) return;
      setLoadingThreads(true);
      setError('');
      try {
        const res = await patientDetailsApi.listConsultations(spruceEntityId);
        const list: ConsultationThread[] = res.data?.data || [];
        setThreads(list);
        if (autoSelect) {
          setSelectedThreadId((prev) => prev || (list.length ? list[0].id : null));
        }
      } catch {
        setError('Failed to load consultations.');
      } finally {
        setLoadingThreads(false);
      }
    },
    [spruceEntityId],
  );

  const loadMessages = useCallback(async (threadId: string) => {
    setLoadingMessages(true);
    try {
      const res = await patientDetailsApi.getConsultationMessages(threadId);
      setMessages(res.data?.data || []);
    } catch {
      setError('Failed to load messages.');
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  // Reset everything when switching patients.
  useEffect(() => {
    setThreads([]);
    setSelectedThreadId(null);
    setMessages([]);
    setInput('');
    setError('');
    if (spruceEntityId) loadThreads(true);
  }, [spruceEntityId, loadThreads]);

  useEffect(() => {
    if (selectedThreadId) loadMessages(selectedThreadId);
    else setMessages([]);
  }, [selectedThreadId, loadMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  // Host-driven thread selection (e.g. from the History timeline "Open"
  // action). Ensures the thread list is loaded, then selects the requested
  // thread. Keyed on openThreadNonce so repeated opens of the same thread work.
  useEffect(() => {
    if (!openThreadId || !spruceEntityId) return;
    let cancelled = false;
    (async () => {
      if (!threads.some((t) => t.id === openThreadId)) {
        await loadThreads(false);
      }
      if (!cancelled) setSelectedThreadId(openThreadId);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openThreadId, openThreadNonce, spruceEntityId]);

  const handleNewThread = async () => {
    if (!spruceEntityId) return;
    setCreating(true);
    setError('');
    try {
      const res = await patientDetailsApi.createConsultation(spruceEntityId, {
        patientName: patientName || undefined,
      });
      const thread: ConsultationThread | null = res.data?.data || null;
      if (thread) {
        setThreads((prev) => [thread, ...prev]);
        setSelectedThreadId(thread.id);
        setMessages([]);
      }
    } catch {
      setError('Failed to start a new consultation.');
    } finally {
      setCreating(false);
    }
  };

  const handleSend = async () => {
    const content = input.trim();
    if (!content || sending) return;

    let threadId = selectedThreadId;
    // No thread selected yet — create one on the fly so the doctor can just
    // start typing.
    if (!threadId) {
      if (!spruceEntityId) return;
      try {
        const res = await patientDetailsApi.createConsultation(spruceEntityId, {
          patientName: patientName || undefined,
        });
        const thread: ConsultationThread | null = res.data?.data || null;
        if (!thread) {
          setError('Failed to start a new consultation.');
          return;
        }
        threadId = thread.id;
        setThreads((prev) => [thread, ...prev]);
        setSelectedThreadId(thread.id);
      } catch {
        setError('Failed to start a new consultation.');
        return;
      }
    }

    setSending(true);
    setError('');
    // Optimistically show the doctor's message.
    const optimistic: ConsultationMessage = {
      id: `tmp_${Date.now()}`,
      threadId,
      role: 'user',
      content,
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);
    setInput('');
    try {
      const res = await patientDetailsApi.sendConsultationMessage(threadId, { content });
      if (res.data?.success === false) {
        setError(res.data?.error || 'The AI assistant is unavailable. Please try again.');
      }
      // Re-fetch the authoritative message list (replaces the optimistic copy
      // and includes the assistant reply).
      await loadMessages(threadId);
      await loadThreads(false);
    } catch (e: any) {
      setError(
        e?.response?.data?.error ||
          'The AI assistant is unavailable. Your message was saved — please try again.',
      );
      await loadMessages(threadId);
    } finally {
      setSending(false);
    }
  };

  const handleStartRename = (thread: ConsultationThread) => {
    setRenamingId(thread.id);
    setRenameText(thread.title);
  };

  const handleCommitRename = async () => {
    const title = renameText.trim();
    if (!renamingId || !title) {
      setRenamingId(null);
      return;
    }
    const id = renamingId;
    setThreads((prev) => prev.map((t) => (t.id === id ? { ...t, title } : t)));
    setRenamingId(null);
    try {
      await patientDetailsApi.renameConsultation(id, title);
    } catch {
      setError('Failed to rename consultation.');
      loadThreads(false);
    }
  };

  const handleCopy = async (id: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId((c) => (c === id ? null : c)), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };

  if (!spruceEntityId) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
        Select a patient to start an AI consultation.
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 1 : 2,
        // Desktop fills the tab body so the two columns scroll independently;
        // mobile is a single scrolling column (the patient pane owns scroll),
        // so it just needs to fill at least the visible area.
        ...(isMobile
          ? { minHeight: '100%', overflowX: 'hidden' }
          : { height: '100%', minHeight: 0 }),
      }}
    >
      {/* Mobile: compact thread switcher. The fixed-width side list does not
          fit a phone — it forced horizontal scroll and one-word-per-line
          chat — so threads move into a dropdown with a "New" button. */}
      {isMobile && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            select
            size="small"
            fullWidth
            value={selectedThreadId || ''}
            onChange={(e) => setSelectedThreadId(e.target.value || null)}
            SelectProps={{ displayEmpty: true }}
            sx={{ flex: 1, minWidth: 0 }}
          >
            {threads.length === 0 && (
              <MenuItem value="" disabled>
                No consultations yet
              </MenuItem>
            )}
            {threads.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.title}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="outlined"
            size="small"
            startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
            onClick={handleNewThread}
            disabled={creating}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            New
          </Button>
        </Box>
      )}

      {/* Thread list (desktop only) */}
      {!isMobile && (
      <Paper
        variant="outlined"
        sx={{ width: 260, flexShrink: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Button
            fullWidth
            variant="contained"
            size="small"
            startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
            onClick={handleNewThread}
            disabled={creating}
          >
            New consultation
          </Button>
        </Box>
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {loadingThreads ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={20} />
            </Box>
          ) : threads.length === 0 ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', p: 2, textAlign: 'center' }}>
              No consultations yet.
            </Typography>
          ) : (
            <List dense disablePadding>
              {threads.map((thread) => (
                <ListItemButton
                  key={thread.id}
                  selected={thread.id === selectedThreadId}
                  onClick={() => setSelectedThreadId(thread.id)}
                  sx={{ alignItems: 'flex-start' }}
                >
                  {renamingId === thread.id ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                      <TextField
                        value={renameText}
                        onChange={(e) => setRenameText(e.target.value)}
                        size="small"
                        autoFocus
                        fullWidth
                        variant="standard"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleCommitRename();
                          if (e.key === 'Escape') setRenamingId(null);
                        }}
                      />
                      <IconButton size="small" onClick={handleCommitRename} aria-label="Save name">
                        <CheckIcon fontSize="inherit" />
                      </IconButton>
                      <IconButton size="small" onClick={() => setRenamingId(null)} aria-label="Cancel rename">
                        <CloseIcon fontSize="inherit" />
                      </IconButton>
                    </Box>
                  ) : (
                    <>
                      <ListItemText
                        primary={thread.title}
                        secondary={
                          thread.lastMessageAt || thread.updatedAt
                            ? formatDateTime(thread.lastMessageAt || thread.updatedAt)
                            : undefined
                        }
                        primaryTypographyProps={{
                          noWrap: true,
                          fontSize: '0.85rem',
                          fontWeight: thread.id === selectedThreadId ? 600 : 400,
                        }}
                        secondaryTypographyProps={{ fontSize: '0.7rem' }}
                      />
                      <Tooltip title="Rename">
                        <IconButton
                          size="small"
                          edge="end"
                          aria-label="Rename consultation"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStartRename(thread);
                          }}
                        >
                          <EditIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>
      </Paper>
      )}

      {/* Chat pane */}
      <Paper
        variant="outlined"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          // On mobile the patient pane is the single scroll owner, so the chat
          // body flows in the page scroll (no nested scroll, no border).
          overflow: isMobile ? 'visible' : 'hidden',
          ...(isMobile ? { border: 0, bgcolor: 'transparent' } : {}),
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflowY: isMobile ? 'visible' : 'auto',
            p: isMobile ? 0 : 2,
            // Clear the sticky composer so the last bubble is never hidden.
            ...(isMobile ? { pb: 'calc(76px + env(safe-area-inset-bottom))' } : {}),
          }}
        >
          {loadingMessages ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : messages.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
              <SmartToyIcon sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
              <Typography variant="body2">
                Ask anything about {patientName || 'this patient'} — clinical questions, a draft message to
                send, or scheduling suggestions. The assistant uses this patient's chart, documents and recent
                history.
              </Typography>
            </Box>
          ) : (
            messages.map((msg) => {
              const isAssistant = msg.role === 'assistant';
              const { before, draft } = isAssistant
                ? extractDrafts(msg.content)
                : { before: msg.content, draft: null };
              const schedule = isAssistant ? extractScheduling(msg.content) : null;
              return (
                <Box
                  key={msg.id}
                  sx={{
                    display: 'flex',
                    flexDirection: isAssistant ? 'row' : 'row-reverse',
                    gap: 1,
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: '50%',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: isAssistant ? 'primary.main' : 'grey.300',
                      color: isAssistant ? 'primary.contrastText' : 'text.primary',
                    }}
                  >
                    {isAssistant ? <SmartToyIcon fontSize="small" /> : <PersonIcon fontSize="small" />}
                  </Box>
                  <Box sx={{ maxWidth: '78%', minWidth: 0 }}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.25,
                        bgcolor: isAssistant ? 'background.paper' : 'primary.50',
                        borderColor: isAssistant ? 'divider' : 'primary.100',
                      }}
                    >
                      {before && (
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {before}
                        </Typography>
                      )}
                      {draft && (
                        <Box
                          sx={{
                            mt: before ? 1 : 0,
                            p: 1,
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1,
                            bgcolor: 'grey.50',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                              Draft message
                            </Typography>
                            <Button
                              size="small"
                              startIcon={copiedId === msg.id ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                              onClick={() => handleCopy(msg.id, draft)}
                            >
                              {copiedId === msg.id ? 'Copied' : 'Copy'}
                            </Button>
                          </Box>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {draft}
                          </Typography>
                        </Box>
                      )}
                      {schedule && onCreateAppointment && (
                        <Box
                          sx={{
                            mt: before || draft ? 1 : 0,
                            p: 1,
                            border: 1,
                            borderColor: 'primary.100',
                            borderRadius: 1,
                            bgcolor: 'primary.50',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
                              Scheduling suggestion
                            </Typography>
                            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                              {schedule.text}
                            </Typography>
                          </Box>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<EventAvailableIcon fontSize="small" />}
                            onClick={() => onCreateAppointment(schedule)}
                            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                          >
                            Create appointment
                          </Button>
                        </Box>
                      )}
                      {isAssistant && !draft && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                          <Button
                            size="small"
                            startIcon={copiedId === msg.id ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                            onClick={() => handleCopy(msg.id, msg.content)}
                          >
                            {copiedId === msg.id ? 'Copied' : 'Copy'}
                          </Button>
                        </Box>
                      )}
                    </Paper>
                    {msg.createdAt && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, px: 0.5 }}>
                        {formatDateTime(msg.createdAt)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })
          )}
          {sending && (
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, mb: 2 }}>
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                }}
              >
                <SmartToyIcon fontSize="small" />
              </Box>
              <Paper
                variant="outlined"
                sx={{ p: 1.25, display: 'flex', alignItems: 'center', gap: 1, borderColor: 'divider' }}
              >
                <Box
                  aria-label="Assistant is thinking"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    '@keyframes aiTypingBounce': {
                      '0%, 80%, 100%': { transform: 'translateY(0)', opacity: 0.4 },
                      '40%': { transform: 'translateY(-4px)', opacity: 1 },
                    },
                    '& span': {
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: 'text.secondary',
                      animation: 'aiTypingBounce 1.4s infinite ease-in-out both',
                    },
                    '& span:nth-of-type(2)': { animationDelay: '0.2s' },
                    '& span:nth-of-type(3)': { animationDelay: '0.4s' },
                  }}
                >
                  <span />
                  <span />
                  <span />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  AI is thinking…
                </Typography>
              </Paper>
            </Box>
          )}
          <div ref={messagesEndRef} />
        </Box>

        {error && (
          <Typography variant="caption" color="error" sx={{ px: 2, pb: 0.5 }}>
            {error}
          </Typography>
        )}

        {/* Composer */}
        <Box
          sx={{
            p: 1.5,
            borderTop: 1,
            borderColor: 'divider',
            display: 'flex',
            gap: 1,
            alignItems: 'flex-end',
            // On mobile pin to the bottom of the scroll viewport (rides the
            // iOS keyboard via `position: sticky`), full-bleed with a
            // translucent fill — matching the Messages tab composer.
            ...(isMobile
              ? {
                  position: 'sticky',
                  bottom: 0,
                  zIndex: 3,
                  bgcolor: 'rgba(255,255,255,0.92)',
                  backdropFilter: 'saturate(180%) blur(10px)',
                  WebkitBackdropFilter: 'saturate(180%) blur(10px)',
                  pb: 'max(env(safe-area-inset-bottom), 12px)',
                }
              : {}),
          }}
        >
          <TextField
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about this patient, request a draft message, or scheduling help…"
            multiline
            maxRows={5}
            fullWidth
            size="small"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
          />
          <Button
            variant="contained"
            onClick={handleSend}
            disabled={sending || !input.trim()}
            sx={{ minWidth: 0, px: 2, height: 40 }}
            aria-label="Send"
          >
            <SendIcon fontSize="small" />
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default AIConsultationPanel;
